import { chromeWord } from '@shared/branding';
import { useBranding } from '../state';

/**
 * The `review.` lockup.
 *
 * One word, one stop, same as its siblings — Compose's title bar says
 * `compose.`, Snap's says `snap.` and Cloud's says `cloud.`. The design handoff
 * is explicit that this is *not* the design system's `Wordmark` component, which
 * renders the company name: it is the per-app lockup, so it stays hand-set here.
 *
 * The stop is teal on the dark author chrome, which is the brand's reversed
 * lockup, and violet on the light reviewer page. That is the one rule worth
 * keeping: teal is a signal colour everywhere else in this app, and the full
 * stop is the single place it appears decoratively.
 *
 * ── Under an organisation's branding ────────────────────────────────────────
 * The word and the tile come from the organisation when it has set them. The
 * two stops need no change and deliberately get none: the reversed lockup's
 * teal is a locked signal colour and stays Mauvely's, while the primary
 * lockup's stop is already `--primary`, which *is* the accent — so it follows
 * the customer's colour by construction, which is what the primary lockup is
 * supposed to do.
 */

interface Props {
  size?: number;
  /** The dark author chrome takes the reversed lockup with a teal stop. */
  reversed?: boolean;
  showMark?: boolean;
}

export function Wordmark({ size = 15, reversed = false, showMark = true }: Props) {
  const display = "var(--font-display), 'Baloo 2', system-ui";
  const branding = useBranding();
  const word = chromeWord(branding, 'review');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: showMark ? 7 : 0 }}>
      {showMark && (
        <img
          src={branding?.logoUrl ?? '/brand/review.svg'}
          alt=""
          aria-hidden="true"
          width={size + 5}
          height={size + 5}
          // `objectFit` because a customer's logo is whatever shape they
          // uploaded, and the tile is square.
          style={{ borderRadius: 5, flex: 'none', display: 'block', objectFit: 'contain' }}
        />
      )}
      <span
        style={{
          fontFamily: display,
          fontWeight: 800,
          fontSize: size,
          letterSpacing: '-.01em',
          color: 'var(--text-strong)'
        }}
      >
        {word}
        <span style={{ color: reversed ? 'var(--signal)' : 'var(--primary)' }}>.</span>
      </span>
    </span>
  );
}
